import { StatusData } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { keepNewestPerKey, NewestEntry } from "@/pages/live-data/utilities/newestReading";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/**
 * How long the day's data is considered fresh.
 *
 * A day-long window barely moves from one minute to the next, and it is a much larger query
 * than the live window, so it is not polled on a timer — it refetches when the query becomes
 * stale on remount (a vehicle change gives it a fresh query key) or on a manual refresh.
 */
const STALE_MS = 5 * 60 * 1000;

const byDiagnostic = (record: StatusData) => record.diagnostic?.id ?? "";
const readAt = (record: StatusData) => record.dateTime;

/**
 * The newest reading of every diagnostic a vehicle reported in the last 24 hours.
 *
 * Paged rather than fetched in one call. Geotab returns a date range oldest first and silently
 * truncates it at the results limit, so a single request for a whole day of a busy vehicle
 * returns the *oldest* slice of it — the checklist would then report "not reported" for values
 * that arrived an hour ago, which is the exact opposite of what it is for.
 *
 * Only the newest reading per diagnostic is kept. That is all the checklist ever asks for, and
 * holding a full day of readings for one vehicle in memory is tens of thousands of objects.
 */
export function useDayStatusData(deviceId: string | undefined) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["dayStatusData", activeSessionId, deviceId],
    queryFn: async ({ signal }) => {
      const toDate = new Date();
      const fromDate = subDays(toDate, 1);
      const newest = new Map<string, NewestEntry<StatusData>>();

      await fetchStatusDataComplete(geotabClient, {
        tasks: [{ key: "day", deviceId: deviceId as string }],
        fromDate,
        toDate,
        signal,
        onPage: (_key, page) => keepNewestPerKey(newest, page, byDiagnostic, readAt),
      });

      return [...newest.values()].map(entry => entry.item);
    },
    enabled: !!deviceId,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}
